import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, AlertTriangle, Loader2, X, Users } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { RadioGroup } from '@/components/ui/radio-group';
import { ModalShell } from '@/components/ModalShell';

import { DOMAIN_ORDER, DOMAIN_SPECS, computePositiveFinding, type DomainAnswer } from './domains';
import { householdApi, type HouseholdMemberView } from './household.api';
import type { HrsnDomain, SubmitScreeningInput, TimeCategory } from '@/types/api';
import { ApiError } from '@/lib/api-client';
import { useT, useLang } from '@/i18n';
import { cn } from '@/lib/utils';

interface SubmitModalProps {
  open: boolean;
  onClose: () => void;
  answers: Partial<Record<HrsnDomain, DomainAnswer>>;
  elapsedSec: number;
  isSubmitting: boolean;
  submitError: Error | null;
  onConfirm: (input: SubmitScreeningInput) => void;
  /** Used to load captured household members for the summary. */
  screeningId?: string;
}

const TIME_CATEGORY_OPTIONS: Array<{ value: TimeCategory; key: 'submit.timeUnder8' | 'submit.time8to22' | 'submit.time22to37' | 'submit.time37plus'; lowerSec: number }> = [
  { value: 'under_8', key: 'submit.timeUnder8', lowerSec: 0 },
  { value: '8_to_22', key: 'submit.time8to22', lowerSec: 8 * 60 },
  { value: '22_to_37', key: 'submit.time22to37', lowerSec: 22 * 60 },
  { value: '37_plus', key: 'submit.time37plus', lowerSec: 37 * 60 },
];

export function SubmitModal({
  open,
  onClose,
  answers,
  elapsedSec,
  isSubmitting,
  submitError,
  onConfirm,
  screeningId,
}: SubmitModalProps): JSX.Element | null {
  const t = useT();
  const lang = useLang();
  const [timeCategory, setTimeCategory] = useState<TimeCategory>('22_to_37');
  const [nextStepNavigator, setNextStepNavigator] = useState(true);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    if (elapsedSec >= 22 * 60 && elapsedSec < 37 * 60) setTimeCategory('22_to_37');
    else if (elapsedSec >= 37 * 60) setTimeCategory('37_plus');
    else if (elapsedSec >= 8 * 60) setTimeCategory('8_to_22');
    else setTimeCategory('under_8');
  }, [open, elapsedSec]);

  useEffect(() => {
    if (!open) return;
    const original = document.activeElement as HTMLElement | null;
    dialogRef.current?.focus();
    return () => {
      original?.focus?.();
    };
  }, [open]);

  const householdQ = useQuery({
    queryKey: ['screening', screeningId ?? '', 'household-members'],
    queryFn: () => householdApi.listForScreening(screeningId as string),
    enabled: !!screeningId && open,
    staleTime: 0,
  });
  const householdMembers: HouseholdMemberView[] = householdQ.data ?? [];

  const findings = useMemo(() => {
    return DOMAIN_ORDER.map((d) => {
      const a = answers[d];
      if (!a) return { domain: d, status: 'missing' as const };
      const positive = computePositiveFinding(d, a);
      return { domain: d, status: positive ? ('positive' as const) : ('clear' as const) };
    });
  }, [answers]);

  const missingCount = findings.filter((f) => f.status === 'missing').length;

  const declaredLowerSec =
    TIME_CATEGORY_OPTIONS.find((o) => o.value === timeCategory)?.lowerSec ?? 0;
  const antiFraudWarning = elapsedSec < declaredLowerSec;

  const blockSubmit =
    missingCount > 0 || isSubmitting;

  if (!open) return null;

  const handleSubmit = (): void => {
    if (blockSubmit) return;
    onConfirm({
      timeCategory,
      nextStepNavigator,
      // findhelpSubmitted will be set later by the future uploader role (Wave B).
      // Send false here; backend stores it but no longer blocks on it.
      findhelpSubmitted: false,
    });
  };

  return (
    <ModalShell
      open={open}
      onClose={() => !isSubmitting && onClose()}
      ariaLabelledBy="submit-modal-title"
      onBackdropClick={() => !isSubmitting && onClose()}
    >
      {/* F5-R1a — «Revisar y enviar» al brand: título Montserrat, hairlines,
          semáforo sage/amber/red. Título, estructura y lógica intactos. */}
      <header className="sticky top-0 bg-white border-b border-nav-hair px-6 py-5 flex items-center justify-between rounded-t-[14px] z-10">
        <div>
          <h2 id="submit-modal-title" className="font-head text-xl font-bold text-nav-navy tracking-tight">{t('submit.title')}</h2>
          <p className="text-xs text-nav-slate mt-0.5">{t('submit.summaryHeading')}</p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          disabled={isSubmitting}
          aria-label={t('submit.close')}
        >
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="modal-scroll flex-1 overflow-y-auto font-body text-nav-navy">
        <div
          ref={dialogRef}
          tabIndex={-1}
          className="px-6 py-6 space-y-6"
        >
          <section>
            <p className="font-head font-bold text-[10.5px] tracking-[.12em] uppercase text-nav-teal mb-3">{t('submit.summaryHeading')}</p>
            <div className="flex gap-3 mb-3">
              <SummaryStat label={lang === 'es' ? 'Duración' : 'Duration'} value={fmtElapsed(elapsedSec)} />
              <SummaryStat
                label={t('submit.summaryHeading')}
                value={`${DOMAIN_ORDER.length - missingCount} / ${DOMAIN_ORDER.length}`}
                tone={missingCount === 0 ? 'sage' : 'red'}
              />
              <SummaryStat
                label={t('submit.findingPositive')}
                value={String(findings.filter((f) => f.status === 'positive').length)}
              />
            </div>
            <ul className="text-sm space-y-1">
              {findings.map((f) => (
                <li key={f.domain} className="flex items-center gap-2">
                  {f.status === 'missing' ? (
                    <AlertTriangle className="h-3.5 w-3.5 text-nav-red shrink-0" />
                  ) : (
                    <CheckCircle2
                      className={cn(
                        'h-3.5 w-3.5 shrink-0',
                        f.status === 'positive' ? 'text-nav-amberText' : 'text-nav-sageMid',
                      )}
                    />
                  )}
                  <span className="text-nav-navy">{lang === 'es' ? DOMAIN_SPECS[f.domain].titleEs : DOMAIN_SPECS[f.domain].title}</span>
                  <span
                    className={cn(
                      'text-xs ml-auto',
                      f.status === 'missing'
                        ? 'text-nav-red'
                        : f.status === 'positive'
                          ? 'text-nav-amberText'
                          : 'text-nav-slate',
                    )}
                  >
                    {f.status === 'missing'
                      ? t('submit.findingMissing')
                      : f.status === 'positive'
                        ? t('submit.findingPositive')
                        : t('submit.findingClear')}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {/* Household members captured */}
          <section>
            <p className="font-head font-bold text-[10.5px] tracking-[.12em] uppercase text-nav-teal mb-3">
              {lang === 'es' ? 'Miembros del hogar capturados' : 'Household members captured'}
            </p>
            {householdMembers.length === 0 ? (
              <p className="text-sm text-nav-slate">
                {lang === 'es' ? 'Ninguno capturado.' : 'None captured.'}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {householdMembers.map((m) => (
                  <li key={m.id} className="flex items-center gap-2 text-sm">
                    <Users className="h-3.5 w-3.5 text-nav-teal shrink-0" />
                    <span className="text-nav-navy">{m.fullName}</span>
                    <span className="text-xs text-nav-slate">
                      ({lang === 'es' ? 'nac.' : 'born'} {m.dobYear || '—'})
                    </span>
                    {m.medicaidId && (
                      <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded-full font-head text-[10px] font-bold bg-nav-sageTint text-nav-sageText">
                        {lang === 'es' ? 'Medicaid' : 'Medicaid'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="space-y-2">
            <Label>{t('submit.timeCategoryLabel')}</Label>
            <RadioGroup
              name="time_category"
              value={timeCategory}
              onChange={(v) => setTimeCategory(v as TimeCategory)}
              options={TIME_CATEGORY_OPTIONS.map((o) => ({ value: o.value, label: t(o.key) }))}
            />
            {antiFraudWarning && (
              <Alert variant="info" role="status">
                <AlertTitle>Time bucket vs measured</AlertTitle>
                <AlertDescription>
                  System measured {fmtElapsed(elapsedSec)}. You are declaring a higher bucket — this is fine: the supervisor handling FindHelp will validate before submitting there.
                </AlertDescription>
              </Alert>
            )}
          </section>

          <section className="space-y-1.5">
            <Label>{t('submit.nextStepNavigatorLabel')}</Label>
            <RadioGroup
              name="next_step"
              value={nextStepNavigator ? 'yes' : 'no'}
              onChange={(v) => setNextStepNavigator(v === 'yes')}
              options={[
                { value: 'yes', label: t('submit.nextStepNavigatorYes') },
                { value: 'no', label: t('submit.nextStepNavigatorNo') },
              ]}
              inline
            />
          </section>

          {submitError && (
            <Alert variant="error" role="alert">
              <AlertTitle>{t('submit.error')}</AlertTitle>
              <AlertDescription>
                {/* R1: el 404 aca casi siempre es el reaper (15 min sin
                    heartbeat borra el screening abierto y devuelve el caso a
                    la cola) — el crudo «Screening N not found» no le dice
                    nada a la agente. */}
                {submitError instanceof ApiError && submitError.status === 404
                  ? t('submit.expiredByIdle')
                  : submitError.message}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      <footer className="sticky bottom-0 bg-white border-t border-nav-hair px-6 py-4 flex items-center justify-end gap-2 rounded-b-[14px]">
        <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
          {t('submit.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={blockSubmit} size="lg">
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
          {isSubmitting ? t('submit.submitting') : t('submit.button')}
        </Button>
      </footer>
    </ModalShell>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: 'sage' | 'red';
}): JSX.Element {
  const toneCls =
    tone === 'sage'
      ? 'border-nav-sage bg-nav-sageTint'
      : tone === 'red'
        ? 'border-nav-red bg-nav-redTint'
        : 'border-nav-card bg-nav-soft';
  return (
    <div className={cn('flex-1 rounded-[10px] border-[1.5px] p-2.5', toneCls)}>
      <p className="font-head text-[10px] font-bold text-nav-slate uppercase tracking-wide">{label}</p>
      <p className="font-head text-lg font-bold text-nav-navy tabular-nums">{value}</p>
    </div>
  );
}

function fmtElapsed(sec: number): string {
  const mm = Math.floor(sec / 60);
  const ss = sec % 60;
  return `${mm}m ${String(ss).padStart(2, '0')}s`;
}
